import { BefMessageServiceContext, BefRpcContext } from "@beflam/bef-core";
import { useCallback, useContext, useState } from "react";
import { PAGE_SIZE, RPC_ASSEMBLY, RPC_MODULE } from "./constants";

interface UserListState {
    data: any[];
    dataSource: any[];
    currentPage: number;
    selectedRowKeys: any[];
    selectRecords: any[];
    columnWidth: Record<string, number>;
}

interface UseUserListOptions {
    initialWheres: any[];
    pageSize?: number;
    mergeFilterWheres?: (baseWheres: any[], filterWheres: any[]) => any[];
}

const initialState: UserListState = {
    data: [],
    dataSource: [],
    currentPage: 1,
    selectedRowKeys: [],
    selectRecords: [],
    columnWidth: {},
};

export function useUserList(options: UseUserListOptions) {
    const { initialWheres, pageSize = PAGE_SIZE, mergeFilterWheres } = options;
    const [state, setState] = useState<UserListState>(initialState);
    const befRpc = useContext(BefRpcContext);
    const messageService = useContext(BefMessageServiceContext);

    const updateState = useCallback((updater: Partial<UserListState> | ((prev: UserListState) => Partial<UserListState>)) => {
        setState(prev => {
            const partial = typeof updater === 'function' ? updater(prev) : updater;
            return { ...prev, ...partial };
        });
    }, []);

    const paginateData = useCallback((data: any[], current: number) => {
        return data.slice((current - 1) * pageSize, current * pageSize);
    }, [pageSize]);

    const onPaginationChange = useCallback((current: number) => {
        setState(prev => ({
            ...prev,
            dataSource: paginateData(prev.data, current),
            currentPage: current,
        }));
    }, [paginateData]);

    const refresh = useCallback(async () => {
        try {
            const result = await befRpc.callRpcAsync<any[]>("", RPC_ASSEMBLY, RPC_MODULE, "List", { Wheres: initialWheres });
            const paginated = paginateData(result, 1);
            setState({
                data: result,
                dataSource: paginated,
                selectRecords: [],
                selectedRowKeys: [],
                currentPage: 1,
                columnWidth: {},
            });
        } catch (error) {
            messageService.showMessage(messageService.handleErr(error, "用户列表获取"));
        }
    }, [initialWheres, befRpc, messageService, paginateData]);

    const refreshWithFilter = useCallback(async (filterWheres: any) => {
        try {
            const _wheres = mergeFilterWheres
                ? mergeFilterWheres(initialWheres, filterWheres)
                : [...initialWheres].concat(filterWheres || []);
            const result = await befRpc.callRpcAsync<any[]>("", RPC_ASSEMBLY, RPC_MODULE, "List", { Wheres: _wheres });
            const paginated = paginateData(result, 1);
            setState({
                data: result,
                dataSource: paginated,
                selectRecords: [],
                selectedRowKeys: [],
                currentPage: 1,
                columnWidth: {},
            });
        } catch (error) {
            messageService.showMessage(messageService.handleErr(error, "用户列表获取"));
        }
    }, [initialWheres, befRpc, messageService, mergeFilterWheres, paginateData]);

    const onSelectionChange = useCallback((selectedKeys: any[], records: any[]) => {
        setState(prev => ({
            ...prev,
            selectedRowKeys: selectedKeys,
            selectRecords: records,
        }));
    }, []);

    const onColumnResize = useCallback((dataIndex: string, value: number) => {
        setState(prev => {
            const newData = { ...prev.columnWidth };
            newData[dataIndex] = (newData[dataIndex] ?? 0) + value;
            return { ...prev, columnWidth: newData };
        });
    }, []);

    const setSelected = useCallback((selectedRowKeys: any[], selectRecords: any[]) => {
        setState(prev => ({
            ...prev,
            selectedRowKeys,
            selectRecords,
        }));
    }, []);

    return {
        state,
        refresh,
        refreshWithFilter,
        onPaginationChange,
        onSelectionChange,
        onColumnResize,
        setSelected,
    };
}
